/**
 * Voti dell'allenamento (V15), la scheda Voti: un voto da 1 a 10 a mezzi punti per ogni
 * giocatore in rosa alla data, oppure nessuno.
 *
 * Il Mister scrive, l'Admin legge. Si vota solo l'allenamento svolto, come la
 * partita conclusa (V27), anche a distanza di tempo; la presenza accanto al
 * nome aiuta, ma non vincola. Il salvataggio e quello dell'appello: una
 * richiesta per riga, ritentata sugli errori di rete. Annullato o tornato
 * programmato, l'allenamento tiene i voti ma non li fa modificare.
 */

import { Link, Navigate, useParams } from 'react-router-dom';
import { puo } from '@calcio/shared';
import { toast } from 'sonner';
import { InfoIcon, StarIcon } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { chiama } from '../api/client.js';
import { RigaVoto } from '../comuni/RigaVoto.js';
import { useDati } from '../comuni/dati.js';
import { StatoCaricamento } from '../comuni/Testata.js';
import { useCodaRighe } from '../comuni/useCodaRighe.js';
import { mediaVoti } from '../comuni/voti.js';
import { coloreVoto } from '../giocatori/colori-stato.js';
import { annoDi, etichettaRuolo } from '../giocatori/elenchi.js';
import { useContesto } from '../navigazione/contesto.js';
import { NonTrovata } from '../navigazione/NonTrovata.js';
import { ETICHETTA_PRESENZA, type VotiAllenamento as DatiVoti } from './allenamenti.js';
import { BarraScheda, CardVista, StatoSalvataggio } from '../comuni/Riquadri.js';
import { StatoVuoto } from '../comuni/StatoVuoto.js';
import { Nota } from '../comuni/Nota.js';

export function VotiAllenamento() {
  const { ss, id } = useParams<{ ss: string; id: string }>();
  const { utente } = useContesto();
  const legge = Boolean(ss && puo(utente.assegnazioni, 'voto.leggi', ss));
  const { dati, errore } = useDati<DatiVoti>(legge ? `/allenamenti/${id}/voti` : '');

  if (!ss || !legge) return <NonTrovata />;
  if (errore) return <StatoCaricamento errore={errore} />;
  if (!dati || dati.allenamento.id !== id) return <StatoCaricamento errore={null} />;
  if (dati.allenamento.squadraStagioneId !== ss) {
    return (
      <Navigate to={`/sq/${dati.allenamento.squadraStagioneId}/allenamenti/${id}/voti`} replace />
    );
  }
  return (
    <Elenco
      key={id}
      ss={ss}
      voti={dati}
      puoScrivere={puo(utente.assegnazioni, 'voto.scrivi', ss)}
    />
  );
}

function Elenco({ ss, voti, puoScrivere }: { ss: string; voti: DatiVoti; puoScrivere: boolean }) {
  const a = voti.allenamento;
  const scrive = puoScrivere && a.stato === 'svolto';

  const c = useCodaRighe<number>(
    () => Object.fromEntries(voti.rosa.map((g) => [g.id, g.voto])),
    async (giocatoreId, valore) => {
      await chiama(
        `/allenamenti/${a.id}/voti/${giocatoreId}`,
        valore === null ? { metodo: 'DELETE' } : { metodo: 'PUT', corpo: { valore } },
      );
    },
    (rifiuto) => toast.error(rifiuto),
  );

  const valori = voti.rosa.map((g) => c.valore(g.id));
  const valutati = valori.filter((v) => v !== null).length;
  const media = mediaVoti(valori);
  const inSospeso = c.inSospeso();
  const nonSalvate = voti.rosa.filter((g) => c.esito(g.id) === 'non_salvato').length;

  return (
    <>
      <BarraScheda>
        <span className="flex items-center gap-1 text-muted-foreground" title="Giocatori votati">
          <StarIcon aria-hidden className="size-4" />
          <span className="font-semibold text-foreground tabular-nums">
            {valutati}/{voti.rosa.length}
          </span>
          votati
        </span>
        <span className="flex items-center gap-1 text-muted-foreground">
          Media
          {media !== null ? (
            <Badge className={cn('tabular-nums', coloreVoto(media))}>
              {media.toLocaleString('it-IT')}
            </Badge>
          ) : (
            <span className="font-semibold text-foreground">—</span>
          )}
        </span>
        {scrive && (
          <StatoSalvataggio
            inSospeso={inSospeso}
            nonSalvate={nonSalvate}
            onRiprova={() => c.riprova()}
          />
        )}
      </BarraScheda>

      {a.stato === 'annullato' && (
        <Alert>
          <InfoIcon />
          <AlertTitle>Allenamento annullato</AlertTitle>
          <AlertDescription>
            I voti restano, ma si modificano solo dopo averlo ripristinato dal riepilogo.
          </AlertDescription>
        </Alert>
      )}
      {puoScrivere && a.stato === 'programmato' && (
        <Alert>
          <InfoIcon />
          <AlertTitle>Voti non ancora aperti</AlertTitle>
          <AlertDescription className="flex flex-col items-start gap-3">
            <span>
              Si vota solo un allenamento svolto: chiudi l'appello il giorno della seduta.
              {voti.rosa.some((g) => g.voto !== null) &&
                ' I voti già dati restano, ma fino ad allora non si modificano.'}
            </span>
            <Button asChild variant="outline">
              <Link to={`/sq/${ss}/allenamenti/${a.id}/presenze`} replace>
                Vai all'appello
              </Link>
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <CardVista titolo="Rosa" Icona={StarIcon} conteggio={`${valutati}/${voti.rosa.length}`}>
        {scrive && (
          <Nota className="px-1">
            Il voto si dà anche più tardi e anche a chi non è stato rilevato all&apos;appello;
            &quot;—&quot; lo toglie.
          </Nota>
        )}
        {voti.rosa.length === 0 ? (
          <StatoVuoto titolo="Nessun giocatore in rosa a questa data" />
        ) : (
          <ul className="grid gap-2 @3xl:grid-cols-2">
            {voti.rosa.map((g) => (
              <RigaVoto
                key={g.id}
                numero={g.numero}
                ruolo={g.ruoloPreferito}
                nome={`${g.cognome} ${g.nome}`}
                dettaglio={[
                  g.presenza ? ETICHETTA_PRESENZA[g.presenza] : 'Non rilevato',
                  annoDi(g.dataNascita),
                  etichettaRuolo(g.ruoloPreferito),
                ].join(' · ')}
                esito={c.esito(g.id)}
                valore={c.valore(g.id)}
                scrive={scrive}
                onCambio={(v) => c.imposta(g.id, v)}
              />
            ))}
          </ul>
        )}
      </CardVista>
    </>
  );
}
